import { type Component, type JSX, Match, Switch } from "solid-js";
import type { MatchTabState } from "../../utils/match-tab-state";
import {
  MatchTabError,
  MatchTabInvalidOffer,
  MatchTabLoading,
  MatchTabPermissionDenied,
  MatchTabRateLimited,
  MatchTabSemanticUnavailable,
  MatchTabUpsell,
} from "./match-tab-states";

export type MatchTabGateProps = {
  /** The resolved state from `resolveMatchTabState` (T3). */
  state: MatchTabState;
  /** Current plan slug — passed to the upsell impression. */
  plan: string;
  /** Re-issue the failed capabilities/match request. */
  onRetry: () => void;
  /** Navigate back to the search/offers surface (invalid-offer recovery). */
  onBackToSearch: () => void;
  /** The content surfaces for `list` / `empty_pool` / `empty_filtered` (T9). */
  children: JSX.Element;
};

/**
 * Content-scoped gate for the pool tab. Renders the T3 machine's output 1:1: the
 * five terminal states get their dedicated surface; the three content states
 * (`list` / `empty_pool` / `empty_filtered`) fall through to `children` (the T9
 * panel, which owns the count + empty-state discrimination). Because the match
 * query self-gates on `capabilities.enabled`, `upsell`/`loading` never fire a
 * request (AC8 no-call, D16 no-flash), and pool-tab errors stay confined here —
 * the Ricerca tab is a sibling panel, untouched (AC10).
 */
export const MatchTabGate: Component<MatchTabGateProps> = (props) => (
  <Switch fallback={props.children}>
    <Match when={props.state.state === "loading"}>
      <MatchTabLoading />
    </Match>
    <Match when={props.state.state === "upsell"}>
      <MatchTabUpsell plan={props.plan} />
    </Match>
    <Match when={props.state.state === "permission_denied"}>
      <MatchTabPermissionDenied />
    </Match>
    <Match when={props.state.state === "invalid_offer"}>
      <MatchTabInvalidOffer onBackToSearch={props.onBackToSearch} />
    </Match>
    <Match when={props.state.state === "rate_limited"}>
      <MatchTabRateLimited
        retryAfterSeconds={
          props.state.state === "rate_limited"
            ? props.state.retryAfterSeconds
            : undefined
        }
      />
    </Match>
    <Match when={props.state.state === "semantic_unavailable"}>
      <MatchTabSemanticUnavailable onRetry={props.onRetry} />
    </Match>
    <Match when={props.state.state === "error"}>
      <MatchTabError onRetry={props.onRetry} />
    </Match>
  </Switch>
);
